import { RECENT_SEARCH_LIMIT } from '@/constants/search';

const STORAGE_KEY = 'genai-recent-searches';

/**
 * Past search terms, kept in `localStorage`.
 *
 * The miniapp keeps the same list in the host's permanent miniapp storage
 * (`internals.setState({ permanent: true })`), which does not exist here — this
 * site has no host to persist through. `localStorage`, not `session`: a recent
 * search is worth having on the next visit, not just the next page.
 *
 * Every read is defensive. This value is user-writable and survives releases,
 * so anything that isn't a list of non-empty strings is treated as absent
 * rather than trusted into the UI.
 */
const readAll = (): string[] => {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];

  const data: unknown = JSON.parse(raw);
  if (!Array.isArray(data)) return [];

  return data.filter((term): term is string => typeof term === 'string' && term.length > 0);
};

const write = (terms: string[]): void => {
  if (terms.length) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(terms));
    return;
  }
  window.localStorage.removeItem(STORAGE_KEY);
};

/**
 * Reads the stored terms, or an empty list.
 *
 * Swallows: this runs during render as a `useState` initializer, and Safari
 * private mode throws on `localStorage` access alone. A user with no history
 * and a user whose browser won't give us one see the same thing.
 */
export const loadRecentSearches = (): string[] => {
  try {
    return readAll();
  } catch {
    return [];
  }
};

/**
 * Each mutation returns the next list so the caller's state and storage can't
 * drift. A failed write returns the list anyway — recent searches are a
 * convenience, and losing one must not take search down with it.
 */
export const addRecentSearch = (term: string): string[] => {
  const next = [term, ...loadRecentSearches().filter(existing => existing.toLowerCase() !== term.toLowerCase())].slice(0, RECENT_SEARCH_LIMIT);

  try {
    write(next);
  } catch {
    // See above.
  }

  return next;
};

export const removeRecentSearch = (term: string): string[] => {
  const next = loadRecentSearches().filter(existing => existing !== term);

  try {
    write(next);
  } catch {
    // See above.
  }

  return next;
};

export const clearRecentSearches = (): string[] => {
  try {
    write([]);
  } catch {
    // See above.
  }

  return [];
};
